<template lang="pug">
    Tabs.event-list
        el-card.contains-table(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .events-table(v-else)
                //- :row-link-config="eventsPaginated.link"
                paginated-items(
                    scope="ex"
                    type="evnt_event"
                    :loading="loading"
                    :items="eventsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="eventsPaginated.variables"
                    :all-columns="eventsPaginated.allColumns"
                    :active-columns="eventsPaginated.activeColumns"
                    :page-info="eventsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addEvent")
                                    | {{ $t('New Event') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
                    //- the description is rich text: one line of its words, the rest behind an ellipsis
                    template(v-slot:description="{ item }")
                        span.description-cell(:title="plainDescription(item)") {{ plainDescription(item) }}
                    template(v-slot:last_booking="{ item }")
                        span(v-if="item.last_booking_at") {{ lastBookingLabel(item) }}
                        span.no-booking(v-else) —
                    template(v-slot:banner="{ item }")
                        .event-banner(v-if="bannerSrc(item)")
                            img(:src="bannerSrc(item)" :alt="item.name")
                        .event-banner.is-empty(v-else)
                            i.el-icon-fa-image.far
</template>

<i18n>
{
    "de": {
        "yyyy-MM-dd": "dd.MM.yyyy",
        "New Event": "Neues Event",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Event list could not be fetched.": "Eventliste konnte nicht abgerufen werden."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo'

import router from '@/router'
import pinia from '@/pinia';
import { useSetupStore, useEventManagerEventStore } from '@/stores'

import { getFitStringFromProcessObject } from '@/helpers'
import { timestamp2date } from '@/filters'

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/extensions/eventManager/views/Partials/Tabs'
import Error from '@/components/Error'

export default {
    name: 'Events',
    data() {
        return {
            // loading: true,
            error: false,
            disable: {
                showFilters: false,
            }
        }
    },
    components: {
        PaginatedItems,
        Error,
        Tabs
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        },
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.eventsPaginated, 'showFilters') || false
        },
        hasFilters() {
            return ! _.isEmpty(this.filters)
        },
        filters() {
            let filters = _.cloneDeep(this.eventsPaginated.filters)
            delete filters.cms
            return filters
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        eventStore() {
            return useEventManagerEventStore();
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        debounce() {
            return useSetupStore().debounce;
        },
        loading() {
            return this.eventStore.loading;
        },
        eventsPaginated() {
            return this.eventStore.eventsPaginated;
        },
        sort() {
            var variables = this.eventsPaginated.variables;
            var defaultSort = this.eventsPaginated.defaultSort || { field: 'id', direction: 'asc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        }
    },
    methods: {
        // the words of the rich-text description, tags and entities resolved, whitespace collapsed
        plainDescription(item) {
            const html = _.get(item, 'description') || ''
            if (!html) return ''
            const container = document.createElement('div')
            container.innerHTML = html
            return (container.textContent || '').replace(/\s+/g, ' ').trim()
        },

        lastBookingLabel(event) {
            return timestamp2date(event.last_booking_at, this.$t('yyyy-MM-dd') + ', HH:mm')
        },
        // Vorschaubild des Banners: mit Fokuspunkt-Zuschnitt ueber die
        // FileController-Route, sonst die Storage-URL direkt (wie im Formular)
        bannerSrc(event) {
            const banner = _.get(event, 'banner')
            const url = _.get(banner, 'url')
            if (!url) return ''

            const processing = _.get(banner, 'settings.processing')
            if (!processing) return url

            const query = getFitStringFromProcessObject(processing)
            if (!query) return url

            const route = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const table = banner.upload ? 'uploads' : 'images'
            return `${route}/${table}/${banner.id}/${banner.name}?${query}`
        },
        initialize() {
            this.eventStore.updateLoading(true);
            this.refetch();
        },
        addEvent() {
            const params = { id: 'new' }
            router.push({ name: 'event-main', params })
        },
        handleClick(item) {
            const params = { id: item.id }
            router.push({ name: 'event-main', params })
        },
        handleSizeChange(itemsPerPage) {
            this.eventStore.updateLoading(true);
            this.eventStore.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.eventStore.updateLoading(true);
            this.eventStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.eventStore.defineActiveColumns(visibleColumns);
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.eventStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.eventStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            this.eventStore.defineFilterVisibility(value);
            this.saveFilterVisibility({ scope: 'ex', key: 'evnt_event', value })
                .finally(() => this.disable.showFilters = false);
        },
        refetch() {
            // always add the cms id to filters
            const cmsID = this.activeCms.id;
            const filter = {
                key: 'cms',
                match: cmsID,
            };
            this.eventStore.defineFilterValue({ key: 'cms', value: filter });

            this.eventStore.fetchEventsPaginated()
                .then(() => this.eventStore.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err;

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Event list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.eventStore.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                });
        },
        debouncedRefetch: _.debounce(function() { // function scope is needed here
            this.eventStore.updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style lang="sass">
    .error
        padding: 20px

    // Banner-Vorschau in der Event-Liste (16:9). Die Spalte bleibt schmal:
    // Kopfzelle und Inhalt werden auf 90px begrenzt.
    .event-list
        // the description: one line, cut with an ellipsis, the full text as the tooltip
        .description-cell
            display: block
            max-width: 32rem
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis

        th.col-banner,
        td.col-banner
            width: 110px
            max-width: 110px

        .no-booking
            color: $--color-text-placeholder

        .event-banner
            width: 72px
            height: 41px
            border-radius: 3px
            overflow: hidden
            background: $--background-color-base

            img
                display: block
                width: 100%
                height: 100%
                object-fit: cover

            &.is-empty
                display: flex
                align-items: center
                justify-content: center
                border: 1px solid $--border-color-light
                color: $--color-text-placeholder
                font-size: 14px
</style>
